import { useEffect, useRef, useState } from "react"
import { RetellWebClient } from "retell-client-js-sdk"

import { Orb } from "@/components/landing/discovery/orb"
import { cn } from "@/lib/utils"
import { partnerLandingPageAssessmentDemoCallPath } from "@/routes"

type CallState = "connecting" | "live" | "ended" | "failed"

interface TranscriptTurn {
  speaker: "agent" | "caller"
  text: string
}

function csrfToken(): string {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  )
}

/**
 * IMP-504: the live test call, for real. Mounting means the visitor chose to
 * start, so the panel opens on the connecting beat while the backend stands
 * up the assessment's account + provisional receptionist (first demand) and
 * mints a Retell web-call token — the same agent the generated landing page
 * carries. The layout follows the receptionist landing page's call widget:
 * fixed height so End call never moves, a header with the business name and
 * live timer, the live transcript scrolling inside the body, and the panel
 * staying in the conversation as an artifact after the call ends.
 */
export function TestCall({
  landingUid,
  assessmentUid,
  business,
  onAccountLanding,
  onEnd,
}: {
  landingUid: string
  assessmentUid: string | null
  business: string
  /** The demo call is what first creates the account — its landing uid routes
   *  the purchase bar into the expedited (existing-account) checkout. */
  onAccountLanding: (accountLandingUid: string) => void
  onEnd: () => void
}) {
  const [state, setState] = useState<CallState>("connecting")
  const [failure, setFailure] = useState<string | null>(null)
  const [seconds, setSeconds] = useState(0)
  const [transcript, setTranscript] = useState<TranscriptTurn[]>([])
  const clientRef = useRef<RetellWebClient | null>(null)
  const endedRef = useRef(false)
  const bodyRef = useRef<HTMLDivElement>(null)

  // The conversation continues once, however the call ends — hangup, error,
  // or a connect that never happened.
  const finish = useRef((next: CallState, message?: string) => {
    if (endedRef.current) return
    endedRef.current = true
    setFailure(message ?? null)
    setState(next)
    clientRef.current?.stopCall()
    clientRef.current = null
    onEnd()
  }).current

  useEffect(() => {
    let cancelled = false
    const client = new RetellWebClient()
    clientRef.current = client

    client.on(
      "update",
      (update: {
        transcript?: { role: "agent" | "user"; content: string }[]
      }) => {
        if (!Array.isArray(update.transcript)) return
        setTranscript(
          update.transcript.map((turn) => ({
            speaker: turn.role === "agent" ? "agent" : "caller",
            text: turn.content,
          })),
        )
      },
    )
    client.on("call_started", () => setState("live"))
    client.on("call_ended", () => finish("ended"))
    client.on("error", () =>
      finish("failed", "The call hit an error — plenty to go on anyway."),
    )

    void (async () => {
      try {
        if (!assessmentUid) throw new Error("no assessment")
        const response = await fetch(
          partnerLandingPageAssessmentDemoCallPath(landingUid, assessmentUid),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": csrfToken(),
              Accept: "application/json",
            },
            credentials: "same-origin",
          },
        )
        if (!response.ok) {
          const body = (await response.json().catch(() => ({}))) as {
            demo_complete?: boolean
          }
          throw new Error(
            body.demo_complete
              ? "The demo minutes for this receptionist are used up."
              : "Couldn't connect the call.",
          )
        }
        const data = (await response.json()) as {
          access_token: string
          account_landing_uid: string | null
        }
        if (cancelled) return
        if (data.account_landing_uid) onAccountLanding(data.account_landing_uid)
        // Retell's startCall prompts for microphone permission itself.
        await client.startCall({ accessToken: data.access_token })
      } catch (error) {
        if (!cancelled)
          finish(
            "failed",
            error instanceof Error
              ? error.message
              : "Couldn't connect the call.",
          )
      }
    })()

    return () => {
      cancelled = true
      clientRef.current?.stopCall()
      clientRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (state !== "live") return
    const t = setInterval(() => setSeconds((s) => s + 1), 1000)
    return () => clearInterval(t)
  }, [state])

  useEffect(() => {
    bodyRef.current?.scrollTo({
      top: bodyRef.current.scrollHeight,
      behavior: "smooth",
    })
  }, [transcript])

  const over = state === "ended" || state === "failed"
  const lastSpeaker = transcript[transcript.length - 1]?.speaker

  return (
    <div className="border-lp-border lp-msg-in mt-1 flex h-[26rem] w-full flex-col self-start overflow-hidden rounded-xl border bg-white">
      {/* Header: who you're talking to, and the live timer. */}
      <div className="border-lp-border flex items-center gap-2.5 border-b px-4 py-2.5">
        <Orb
          className={cn(
            "size-8",
            state === "live" && lastSpeaker === "agent" && "lp-orb-talk",
          )}
        />
        <div className="min-w-0 flex-1 text-left">
          <p className="text-lp-fg truncate text-[13.5px] font-semibold">
            {business}
          </p>
          <p className="text-lp-muted text-[11.5px]">AI Receptionist</p>
        </div>
        {state !== "connecting" && (
          <span className="text-lp-muted flex items-center gap-1.5 text-[12.5px] tabular-nums">
            <span
              className="size-1.5 rounded-full"
              style={{
                background: over
                  ? "var(--color-lp-border-strong)"
                  : "var(--lp-accent)",
              }}
            />
            {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
          </span>
        )}
      </div>

      {/* Body: connecting beat, then the live transcript. */}
      <div ref={bodyRef} className="flex-1 overflow-y-auto p-4">
        {state === "connecting" ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-lp-muted animate-pulse text-[13.5px]">
              Connecting…
            </p>
          </div>
        ) : state === "failed" && transcript.length === 0 ? (
          <div className="flex h-full items-center justify-center px-6 text-center">
            <p className="text-lp-muted text-[13.5px]">{failure}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {state === "live" && transcript.length === 0 && (
              <p className="text-lp-muted text-center text-[13.5px]">
                Connected — say hello
              </p>
            )}
            {transcript.map((turn, index) => (
              <div
                key={index}
                className={cn(
                  "flex w-full",
                  turn.speaker === "caller" ? "justify-end" : "justify-start",
                )}
              >
                <div
                  className={cn(
                    "max-w-[85%] rounded-2xl px-3 py-2 text-left text-sm leading-relaxed",
                    turn.speaker === "agent"
                      ? "bg-lp-surface text-lp-fg rounded-tl-sm"
                      : "lp-bubble-visitor text-lp-fg rounded-tr-sm",
                  )}
                >
                  {turn.text}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer: End call holds its place; Call ended takes over after. */}
      <div className="border-lp-border flex items-center justify-end border-t px-3 py-2.5">
        {over ? (
          <p className="text-lp-muted mr-auto px-1 text-[12.5px]">Call ended</p>
        ) : (
          <button
            type="button"
            onClick={() => finish("ended")}
            className="border-lp-border text-lp-fg hover:border-lp-border-strong h-9 rounded-full border px-4 text-[13px] font-medium transition-colors"
          >
            {state === "live" ? "End call" : "Cancel"}
          </button>
        )}
      </div>
    </div>
  )
}
